const SPOTIFY_API_BASE = 'https://api.spotify.com/v1';
const DEFAULT_RECOVERY_DELAY_MS = 700;
const MAX_DEVICE_ATTEMPTS = 2;
const DEFAULT_SKIP_STATE_CHECKS = 15;
const DEFAULT_SKIP_CHECK_DELAY_MS = 200;

const waitFor = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds));

const safeMessage = (value, fallback) => {
  if (typeof value !== 'string' || !value.trim()) return fallback;
  return value.replace(/[\r\n]/g, ' ').slice(0, 300);
};

const isSuccess = response => response.ok === true || (response.status >= 200 && response.status < 300);

const readErrorBody = async response => {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text.slice(0, 300) };
  }
};

const spotifyErrorMessage = (body, status) => safeMessage(
  body?.error?.message || body?.error_description || body?.message || body?.raw,
  `Spotify returned HTTP ${status}`
);

const safeRequestDetails = url => {
  const parsed = new URL(url);
  const query = {};
  for (const [key, value] of parsed.searchParams.entries()) {
    query[key] = key === 'device_id' ? '[present]' : value;
  }
  return { endpoint: parsed.pathname, query };
};

export class SpotifyRequestError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = 'SpotifyRequestError';
    this.status = options.status ?? null;
    this.code = options.code || 'spotify_request_failed';
    this.retryAfter = options.retryAfter ?? null;
    this.diagnostic = options.diagnostic || null;
  }
}

export const isSpotifyTrackUri = uri =>
  typeof uri === 'string' && /^spotify:track:[A-Za-z0-9]+$/.test(uri);

export const isRecoverableDeviceError = error =>
  error?.code === 'stale_sdk_device' || error?.status === 404 || error?.status >= 500;

export const shouldStartPlayback = ({ currentTrack, isPlaying }) =>
  !currentTrack || !isPlaying;

export async function restartCurrentTrack(player) {
  if (!player || typeof player.seek !== 'function') {
    throw new SpotifyRequestError('The Spotify SDK player is not ready.', {
      code: 'sdk_player_unavailable'
    });
  }
  await player.seek(0);
}

export const summarizeSdkPlaybackState = state => ({
  currentTrackUri: state?.track_window?.current_track?.uri || null,
  nextTrackCount: Array.isArray(state?.track_window?.next_tracks) ? state.track_window.next_tracks.length : 0,
  skippingDisallowed: state?.disallows?.skipping_next === true
});

export function createTrackTransitionConfirmation(beforeUri, onConfirmed = async () => undefined) {
  let confirmationPromise = null;
  return {
    observe(state) {
      if (confirmationPromise) return confirmationPromise;
      const afterUri = summarizeSdkPlaybackState(state).currentTrackUri;
      if (!afterUri || afterUri === beforeUri) return null;
      confirmationPromise = Promise.resolve()
        .then(() => onConfirmed({ beforeUri, afterUri }))
        .then(confirmation => ({ beforeUri, afterUri, confirmation }));
      return confirmationPromise;
    }
  };
}

export async function skipCurrentTrackWithConfirmation({
  player,
  initialState,
  onConfirmed,
  onConfirmationReady = () => {},
  wait = waitFor,
  maxStateChecks = DEFAULT_SKIP_STATE_CHECKS,
  checkDelayMs = DEFAULT_SKIP_CHECK_DELAY_MS
}) {
  if (!player || typeof player.nextTrack !== 'function' || typeof player.getCurrentState !== 'function') {
    throw new SpotifyRequestError('The Spotify SDK player is not ready.', { code: 'sdk_player_unavailable' });
  }

  const beforeState = initialState || await player.getCurrentState();
  const before = summarizeSdkPlaybackState(beforeState);
  const diagnostic = {
    flow: 'confirmed_sdk_skip',
    beforeUri: before.currentTrackUri,
    afterUri: before.currentTrackUri,
    nextTrackCount: before.nextTrackCount,
    skippingDisallowed: before.skippingDisallowed,
    skipCommand: 'player.nextTrack'
  };
  if (!before.currentTrackUri) {
    throw new SpotifyRequestError('Spotify did not report a current track to skip.', {
      code: 'skip_current_track_unavailable', diagnostic
    });
  }
  if (before.skippingDisallowed) {
    throw new SpotifyRequestError('Spotify reports that skipping is unavailable.', {
      code: 'skip_disallowed', diagnostic
    });
  }

  const confirmation = createTrackTransitionConfirmation(before.currentTrackUri, onConfirmed);
  onConfirmationReady(confirmation);
  await player.nextTrack();

  const checks = Math.max(1, Math.min(50, Number(maxStateChecks) || DEFAULT_SKIP_STATE_CHECKS));
  for (let check = 0; check < checks; check += 1) {
    const state = await player.getCurrentState();
    const confirmed = confirmation.observe(state);
    if (confirmed) {
      const result = await confirmed;
      return {
        ...diagnostic,
        afterUri: result.afterUri,
        stateChecks: check + 1,
        confirmation: result.confirmation
      };
    }
    if (check + 1 < checks) await wait(checkDelayMs);
  }

  throw new SpotifyRequestError('Spotify did not transition to a different track after Skip.', {
    code: 'skip_not_confirmed',
    diagnostic: { ...diagnostic, stateChecks: checks }
  });
}

export async function runPlaybackAttempt(startPlayback, { onSuccess, onFailure } = {}) {
  try {
    const result = await startPlayback();
    onSuccess?.(result);
    return { ok: true, result };
  } catch (error) {
    onFailure?.(error);
    return { ok: false, error };
  }
}

export function createSpotifyRequest({
  getAccessToken,
  refreshAccessToken,
  fetchImpl = fetch,
  getSdkState = () => ({ ready: false, deviceId: null }),
  logger = console
}) {
  return async function spotifyRequest(operation, endpoint, options = {}, retryOn401 = true) {
    const url = new URL(endpoint.startsWith('http') ? endpoint : `${SPOTIFY_API_BASE}${endpoint}`);
    for (const [key, value] of Object.entries(options.query || {})) {
      if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
    }

    const tokenData = await getAccessToken();
    const headers = {
      ...(options.headers || {}),
      Authorization: `Bearer ${tokenData.access_token}`
    };
    let body;
    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(options.body);
    }

    let response;
    try {
      response = await fetchImpl(url.toString(), {
        method: options.method || 'GET',
        headers,
        body
      });
    } catch (cause) {
      const sdkState = getSdkState();
      const diagnostic = {
        spotifyOperation: operation,
        method: options.method || 'GET',
        ...safeRequestDetails(url),
        status: null,
        spotifyMessage: safeMessage(cause?.message, 'Spotify could not be reached.'),
        retryAfter: null,
        deviceIdPresent: Boolean(options.deviceId || sdkState.deviceId),
        sdkReady: Boolean(sdkState.ready),
        ...(options.diagnostic || {})
      };
      logger.error('Spotify operation failed', diagnostic);
      throw new SpotifyRequestError(diagnostic.spotifyMessage, {
        code: 'spotify_network_error', diagnostic
      });
    }

    if (response.status === 401 && retryOn401) {
      await refreshAccessToken();
      return spotifyRequest(operation, endpoint, options, false);
    }

    if (isSuccess(response)) {
      const sdkState = getSdkState();
      logger.info?.('Spotify operation succeeded', {
        spotifyOperation: operation,
        method: options.method || 'GET',
        ...safeRequestDetails(url),
        status: response.status,
        deviceIdPresent: Boolean(options.deviceId || sdkState.deviceId),
        sdkReady: Boolean(sdkState.ready),
        ...(options.diagnostic || {})
      });
      return response;
    }

    const responseBody = await readErrorBody(response);
    const retryAfter = response.status === 429
      ? Math.max(1, Number(response.headers?.get?.('Retry-After')) || 1)
      : null;
    const sdkState = getSdkState();
    const diagnostic = {
      spotifyOperation: operation,
      method: options.method || 'GET',
      ...safeRequestDetails(url),
      status: response.status,
      spotifyMessage: spotifyErrorMessage(responseBody, response.status),
      retryAfter,
      deviceIdPresent: Boolean(options.deviceId || sdkState.deviceId),
      sdkReady: Boolean(sdkState.ready),
      ...(options.diagnostic || {})
    };
    const error = new SpotifyRequestError(diagnostic.spotifyMessage, {
      status: response.status,
      code: response.status === 429 ? 'rate_limited' : `spotify_http_${response.status}`,
      retryAfter,
      diagnostic
    });
    logger.error('Spotify operation failed', diagnostic);
    throw error;
  };
}

export const transferPlaybackToDevice = (spotifyRequest, deviceId, diagnostic = {}) => {
  if (!deviceId) {
    throw new SpotifyRequestError('The Spotify SDK device is not ready.', {
      code: 'sdk_device_unavailable',
      diagnostic: { ...diagnostic, deviceIdPresent: false, sdkReady: false }
    });
  }
  return spotifyRequest('Transfer Playback', '/me/player', {
    method: 'PUT',
    body: { device_ids: [deviceId] },
    deviceId,
    diagnostic
  });
};

const currentDevice = getDevice => {
  const device = getDevice();
  if (!device?.ready || !device.deviceId) {
    throw new SpotifyRequestError('The Spotify SDK device is not ready.', {
      code: 'sdk_device_unavailable',
      diagnostic: { deviceIdPresent: Boolean(device?.deviceId), sdkReady: Boolean(device?.ready) }
    });
  }
  return device;
};

export function playbackUrisForTracks(tracks) {
  if (!Array.isArray(tracks) || tracks.length === 0) {
    throw new SpotifyRequestError('Playback requires at least one track.', { code: 'invalid_track' });
  }
  return tracks.map((track, index) => {
    if (!track || !isSpotifyTrackUri(track.uri) || track.is_playable === false) {
      throw new SpotifyRequestError('Playback received an invalid or unavailable track.', {
        code: track?.is_playable === false ? 'track_unavailable' : 'invalid_track_uri',
        diagnostic: {
          flow: 'normalized_track_playback',
          trackIndex: index,
          uriPresent: typeof track?.uri === 'string' && track.uri.length > 0,
          isPlayable: track?.is_playable !== false
        }
      });
    }
    return track.uri;
  });
}

export async function playTracksOnSdkDevice({ tracks, ...options }) {
  return playUrisOnSdkDevice({ ...options, uris: playbackUrisForTracks(tracks) });
}

export async function playUrisOnSdkDevice({
  spotifyRequest,
  getDevice,
  recoverDevice = async () => {},
  uris,
  startIndex = 0,
  wait = waitFor,
  recoveryDelayMs = DEFAULT_RECOVERY_DELAY_MS,
  maxAttempts = MAX_DEVICE_ATTEMPTS,
  logger = console
}) {
  const playableUris = Array.isArray(uris) ? uris.slice(startIndex) : [];
  if (!playableUris.length || playableUris.some(uri => !isSpotifyTrackUri(uri))) {
    throw new SpotifyRequestError('Playback requires at least one valid Spotify track URI.', { code: 'invalid_track_uri' });
  }

  const attempts = Math.max(1, Math.min(3, Number(maxAttempts) || MAX_DEVICE_ATTEMPTS));
  let lastError;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    const device = currentDevice(getDevice);
    try {
      await spotifyRequest('Start Playback', '/me/player/play', {
        method: 'PUT',
        query: { device_id: device.deviceId },
        body: { uris: playableUris, position_ms: 0 },
        deviceId: device.deviceId,
        diagnostic: {
          flow: 'explicit_track_playback', attempt, transferAttempted: false,
          startAttempted: true, uriCount: playableUris.length,
          requestedTrackUri: playableUris[0]
        }
      });
      return { deviceId: device.deviceId, attempts: attempt, uris: playableUris };
    } catch (error) {
      lastError = error;
      error.diagnostic = {
        ...(error.diagnostic || {}),
        flow: 'explicit_track_playback',
        attempt,
        transferAttempted: false,
        startAttempted: true,
        startSucceeded: false
      };
      logger.error('Spotify playback flow failed', error.diagnostic);
      if (attempt >= attempts || !isRecoverableDeviceError(error)) throw error;
      await recoverDevice(error);
      await wait(recoveryDelayMs);
    }
  }
  throw lastError;
}

export async function addUriToPlaybackQueue({
  spotifyRequest,
  getDevice,
  recoverDevice = async () => {},
  uri,
  wait = waitFor,
  recoveryDelayMs = DEFAULT_RECOVERY_DELAY_MS,
  maxAttempts = MAX_DEVICE_ATTEMPTS
}) {
  if (!isSpotifyTrackUri(uri)) {
    throw new SpotifyRequestError('The queue item is not a valid Spotify track URI.', { code: 'invalid_track_uri' });
  }
  const attempts = Math.max(1, Math.min(3, Number(maxAttempts) || MAX_DEVICE_ATTEMPTS));
  let lastError;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    const device = currentDevice(getDevice);
    try {
      await spotifyRequest('Add Item to Playback Queue', '/me/player/queue', {
        method: 'POST',
        query: { uri, device_id: device.deviceId },
        deviceId: device.deviceId,
        diagnostic: { flow: 'subsequent_track_queue', attempt }
      });
      return { deviceId: device.deviceId, attempts: attempt, uri };
    } catch (error) {
      lastError = error;
      if (attempt >= attempts || !isRecoverableDeviceError(error)) throw error;
      await recoverDevice(error);
      await wait(recoveryDelayMs);
    }
  }
  throw lastError;
}
